

/**
 * ShareCardButton
 *
 * "Share My Picks" CTA on an undercard entry page. Opens a modal with the
 * generated Prediction Card preview, copy-link, and native-share trigger.
 *
 * Note: this only generates and surfaces the card. Claiming the
 * SHARE_PREDICTION_CARD reward happens separately in /earn — the user submits
 * the URL of their resulting post via the SubmitURLModal flow there. We could
 * inline the claim flow here later, but the URL-submission step makes more
 * sense after they've actually shared.
 */

import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { Share2, Link as LinkIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { API_BASE } from '@/lib/api';
import { absoluteCardImageUrl, cardImagePath } from './share-card-url';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetCloseButton,
} from '@/components/ui/sheet';

export interface ShareCardButtonProps {
  /** UndercardEntry.id — the entry whose picks the card summarizes */
  undercardEntryId: string;
  /** Optional override of the card variant — 'prediction' (default) or 'winner' */
  variant?: 'prediction' | 'winner';
  /** Disabled state — e.g. event not yet entered */
  disabled?: boolean;
  className?: string;
}

export function ShareCardButton({
  undercardEntryId,
  variant = 'prediction',
  disabled,
  className,
}: ShareCardButtonProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // jest-client runs on the Jest origin, so card urls must target the API
  // origin; empty API_BASE degrades to a relative path for same-origin dev
  const cardUrl = `${API_BASE}${cardImagePath(variant, undercardEntryId)}`;

  // Absolute URL for copy/share
  const [absoluteCardUrl, setAbsoluteCardUrl] = useState<string>(cardUrl);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setAbsoluteCardUrl(
      absoluteCardImageUrl(API_BASE, window.location.origin, variant, undercardEntryId),
    );
  }, [variant, undercardEntryId]);

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(absoluteCardUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [absoluteCardUrl]);

  const handleShare = useCallback(() => {
    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      void navigator
        .share({
          title: variant === 'winner' ? 'My winning picks on FantasyPRIME' : 'My picks on FantasyPRIME',
          text: variant === 'winner' ? 'Check out my picks' : "Here's my picks for FantasyPRIME",
          url: absoluteCardUrl,
        })
        .catch(() => {
          // User cancelled native share — no-op
        });
    } else {
      handleCopy();
    }
  }, [absoluteCardUrl, variant, handleCopy]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className={cn(
          'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-[11px] transition-all',
          disabled
            ? 'bg-surface-3 text-warm-gray/40 cursor-not-allowed'
            : 'bg-gold/15 text-gold border border-gold/30 hover:bg-gold/25',
          className,
        )}
        aria-haspopup="dialog"
      >
        <Share2 className="w-3.5 h-3.5" />
        Share My Picks
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="max-w-sm" showClose={false}>
          <div className="flex shrink-0 items-center justify-between border-b border-surface-3 p-3">
            <SheetTitle className="text-xs font-bold text-cream">
              {variant === 'winner' ? 'Winner Card' : 'Prediction Card'}
            </SheetTitle>
            <SheetCloseButton className="-my-2 -mr-1.5" />
          </div>

          {/* Card preview — uses the generator route directly. ImageResponse
              returns a PNG so this is a one-shot render. */}
          <div className="bg-surface p-3 flex justify-center">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={cardUrl}
              alt={variant === 'winner' ? 'Winner card preview' : 'Prediction card preview'}
              width={1080}
              height={1350}
              className="w-full h-auto rounded-lg"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>

          <div className="p-3 space-y-2 border-t border-surface-3">
            <button
              onClick={handleShare}
              className="w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-lg bg-gold/15 text-gold border border-gold/30 hover:bg-gold/25 text-xs font-bold"
            >
              <Share2 className="w-3.5 h-3.5" />
              Share
            </button>
            <button
              onClick={handleCopy}
              className={cn(
                'w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-lg border text-xs font-bold transition-colors',
                copied
                  ? 'bg-verdigris/15 text-verdigris border-verdigris/30'
                  : 'bg-surface-3 text-cream border-surface-4 hover:border-surface-4',
              )}
            >
              <LinkIcon className="w-3.5 h-3.5" />
              {copied ? 'Link copied!' : 'Copy image link'}
            </button>
            <p className="text-[10px] text-warm-gray/40 text-center mt-2 leading-relaxed">
              Post the card on X, Instagram, or TikTok and tag <strong>@FantasyPRIME</strong>.<br />
              {/* router link, not <a>: a raw href full-page-navigates off the
                  memory-routed SPA to a path the Jest CDN does not serve */}
              Then paste your post URL on the <Link href="/earn" className="text-gold hover:underline">Earn page</Link> to claim coins.
            </p>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

export default ShareCardButton;
